import { useState } from 'react';
import { User, Phone, Mail, Edit2 } from 'lucide-react';
import apiFetch from '../apiClient';

const inputStyle = {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    color: 'var(--color-text-primary)',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem'
};

const focusHandlers = {
    onFocus: (e) => e.target.style.borderColor = 'var(--color-primary)',
    onBlur: (e) => e.target.style.borderColor = 'var(--color-border)'
};

export function ProfileInfoCard({ user, onSaved }) {
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState({ usuario_nome: user?.usuario_nome || '', usuario_telefone: user?.usuario_telefone || '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const startEditing = () => {
        setForm({ usuario_nome: user?.usuario_nome || '', usuario_telefone: user?.usuario_telefone || '' });
        setError(null);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setError(null);
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await apiFetch('/api/profile/me', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    usuario_nome: form.usuario_nome,
                    usuario_telefone: form.usuario_telefone
                })
            });
            await onSaved?.();
            setIsEditing(false);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card custom-card h-100">
            <div className="card-body p-4">
                <div className="d-flex align-items-start justify-content-between mb-4">
                    <div className="d-flex align-items-center gap-3">
                        <div className="icon-container">
                            <User size={16} className="text-muted-custom" />
                        </div>
                        <h3 className="h5 fw-semibold mb-0">Informações Pessoais</h3>
                    </div>
                    {!isEditing && (
                        <button className="btn btn-outline-custom d-flex align-items-center gap-2" onClick={startEditing}>
                            <Edit2 size={14} /> Editar
                        </button>
                    )}
                </div>

                <div className="row g-3">
                    <div className="col-12 col-md-6">
                        <label className="form-label text-muted-custom small">Nome</label>
                        {isEditing ? (
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Seu nome"
                                value={form.usuario_nome}
                                onChange={(e) => setForm({ ...form, usuario_nome: e.target.value })}
                                style={inputStyle}
                                {...focusHandlers}
                            />
                        ) : (
                            <div className="d-flex align-items-center gap-2 py-2">
                                <User size={15} className="text-muted-custom" />
                                {user?.usuario_nome
                                    ? <span>{user.usuario_nome}</span>
                                    : <span className="text-muted-custom fst-italic">Não informado</span>}
                            </div>
                        )}
                    </div>

                    <div className="col-12 col-md-6">
                        <label className="form-label text-muted-custom small">Telefone</label>
                        {isEditing ? (
                            <input
                                type="tel"
                                className="form-control"
                                placeholder="(11) 99999-0000"
                                value={form.usuario_telefone}
                                onChange={(e) => setForm({ ...form, usuario_telefone: e.target.value })}
                                style={inputStyle}
                                {...focusHandlers}
                            />
                        ) : (
                            <div className="d-flex align-items-center gap-2 py-2">
                                <Phone size={15} className="text-muted-custom" />
                                {user?.usuario_telefone
                                    ? <span>{user.usuario_telefone}</span>
                                    : <span className="text-muted-custom fst-italic">Não informado</span>}
                            </div>
                        )}
                    </div>

                    <div className="col-12">
                        <label className="form-label text-muted-custom small">E-mail</label>
                        <div className="d-flex align-items-center gap-2 py-2">
                            <Mail size={15} className="text-muted-custom" />
                            <span>{user?.usuario_email}</span>
                        </div>
                        <small className="text-muted-custom">E-mail não pode ser alterado nesta versão.</small>
                    </div>
                </div>

                {isEditing && (
                    <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
                        <button className="btn btn-outline-custom" onClick={cancelEditing} disabled={saving}>Cancelar</button>
                        <button className="btn btn-primary-custom" onClick={save} disabled={saving}>Salvar</button>
                    </div>
                )}
                {error && <small className="text-danger-custom d-block mt-2">{error}</small>}
                {success && <small className="text-primary-custom d-block mt-2">Perfil atualizado com sucesso</small>}
            </div>
        </div>
    );
}
